import { GridRowId } from '@mui/x-data-grid/models/gridRows'
import { GridCellEditStopParams } from '@mui/x-data-grid-premium'
import {
  GridColumnVisibilityModel,
  GridRowModel,
  GridRowSelectionModel,
  GridSortModel,
  MuiEvent,
} from '@mui/x-data-grid-pro'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { BackendErrorToastMessage } from 'src/app/Toast/Toast'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import { TransactionListCheckboxProps } from 'src/components/FullTransactionsTable/FullTransactionsTable'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import { useTransactionsColumns } from 'src/components/TransactionsDataGrid/transactionsDataGrid.columns'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { verificationSortKey } from 'src/models/receiptVerification.models'
import { mockTransactionVerificationStatus } from 'src/models/receiptVerificationMock'
import {
  TransactionDateType,
  TransactionListModel,
  TransactionSort,
} from 'src/models/transaction.models'
import { connectedPush } from 'src/navigation/Lnk'
import { transactionDetailUrl } from 'src/navigation/urls'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { usePostSingleTransactionExpenseCategory } from 'src/queries/transactions.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { queryClient } from 'src/queryClient'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'

import {
  canEditExpenseCategory,
  computeExpenseCategoryCellData,
} from './expenseCategory.helpers'
import type {
  ExpenseCategoryCellData,
  ExpenseCategoryFieldKey,
  TransactionsDataGridRowModel,
} from './transactionsDataGrid.types'

export enum TransactionsTableLargeDesigns {
  UserAndCardAndStatus = 'userAndCardAndStatus',
  UserAndCard = 'userAndCard',
  Card = 'card',
  Base = 'base',
}

type TransactionsDataGridProps = {
  apiRef: MutableRefObject<GridApiPro | null>
  transactions: TransactionListModel[]
  infiniteScrollProps: any
  design: TransactionsTableLargeDesigns
  filters: TransactionsFiltersBody
  savedVisibleColumns?: string[]
  onRegisterResetColumnsToDefault?: (reset: () => void) => void
  onUserChangedColumns?: () => void
  rowSelectionModel?: GridRowSelectionModel
  checkboxProps?: TransactionListCheckboxProps
  reportId?: string
  clientHeight: number
  setSort?: (sort: TransactionSort) => void
  defaultColumnVisibilityModel: GridColumnVisibilityModel
  rowHoverActions?: (params: GridRowModel) => React.ReactElement
  rowApprovalActions?: (params: GridRowModel) => React.ReactElement
  context?: TransactionReportType
}

const EDIT_MODE = 'cell'

const TransactionsDataGrid: React.FC<TransactionsDataGridProps> = (props) => {
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const { data: user } = useQueryOwnUser({})
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const featureFlagger = useSelector(selectFeatureFlaggerFromState)

  // ---- Sorting bridge to server sort enum
  const [sortModel, setSortModel] = React.useState<GridSortModel>([])
  useEffect(() => {
    if (sortModel?.length > 0) {
      const { field, sort } = sortModel[0] ?? {}
      const dirAsc = sort === 'asc'
      switch (field) {
        case 'merchant':
          props.setSort?.(
            dirAsc
              ? TransactionSort.merchantNameAsc
              : TransactionSort.merchantNameDesc,
          )
          break
        case 'cardUser':
          props.setSort?.(
            dirAsc
              ? TransactionSort.recipientNameAsc
              : TransactionSort.recipientNameDesc,
          )
          break
        case 'amount':
          props.setSort?.(
            dirAsc ? TransactionSort.amountAsc : TransactionSort.amountDesc,
          )
          break
        case 'receipts':
          // Client-side sort only — no backend enum for verification status
          // yet. MUI sorts the loaded rows locally via the column's valueGetter.
          break
        case 'date':
        default: {
          const byClear = props.filters.dateType === TransactionDateType.Clear
          props.setSort?.(
            byClear
              ? dirAsc
                ? TransactionSort.dateClearAsc
                : TransactionSort.dateClearDesc
              : dirAsc
                ? TransactionSort.dateAuthAsc
                : TransactionSort.dateAuthDesc,
          )
        }
      }
    } else {
      const byClear = props.filters.dateType === TransactionDateType.Clear
      props.setSort?.(
        byClear ? TransactionSort.dateClearDesc : TransactionSort.dateAuthDesc,
      )
    }
  }, [sortModel])

  const dispatch = useDispatch()

  const { mutateAsync: postCategoryLabel, reset: resetPostCategoryLabel } =
    usePostSingleTransactionExpenseCategory()

  const dispatchErrorToast = (e: BackendErrorPayload<string>) => {
    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: <BackendErrorToastMessage error={e} />,
        failureColorScheme: true,
        hideImage: true,
      }),
    )
  }

  // ---- Build columns via hook (uses Redux selectors & queries internally)
  const { columns, pinnedLeft, pinnedRight, ecLoaded, orgExpCats } =
    useTransactionsColumns({
      context: props.context,
      checkboxMinWidth: props.checkboxProps ? 115 : 144,
      filters: { dateType: props.filters?.dateType },
      rowApprovalActions: props.rowApprovalActions,
      rowHoverActions: props.rowHoverActions,
    })

  const pinned = useMemo(
    () => ({ left: pinnedLeft, right: pinnedRight }),
    [pinnedLeft, pinnedRight],
  )

  // ---- Precompute expense-category cell data for each tx (valueGetter/formatter rely on this)
  const expenseCategoryDataMemo = useMemo(() => {
    if (!orgExpCats || !user)
      return new Map<
        string,
        Record<ExpenseCategoryFieldKey, ExpenseCategoryCellData>
      >()

    const categoryDataMap = new Map<
      string,
      Record<ExpenseCategoryFieldKey, ExpenseCategoryCellData>
    >()

    props.transactions.forEach((transaction) => {
      const expenseCategoryCellData: Record<
        ExpenseCategoryFieldKey,
        ExpenseCategoryCellData
      > = {}

      orgExpCats.forEach((category) => {
        const ecKey: ExpenseCategoryFieldKey = `ec:${category.id}`
        expenseCategoryCellData[ecKey] = computeExpenseCategoryCellData(
          transaction,
          category,
          user,
          activeIntegration,
          (k) => k,
          featureFlagger.show_reconnected_erp_expense_category_labels,
        )
      })

      categoryDataMap.set(transaction.id, expenseCategoryCellData)
    })

    return categoryDataMap
  }, [
    props.transactions,
    orgExpCats,
    user?.hasOrgBookkeeperPermissions,
    activeIntegration?.connectedPlatform,
    featureFlagger.show_reconnected_erp_expense_category_labels,
  ])

  // ---- Rows
  const rows: TransactionsDataGridRowModel[] = useMemo(() => {
    const base = props.transactions.map((transaction, i) => ({
      id: transaction.id,
      transaction,
      index: i,
      expenseCategoryCellData:
        expenseCategoryDataMemo.get(transaction.id) || {},
    }))
    // Client-side sort for the Receipts column: the shared DataGrid wrapper
    // pins `sortingMode="server"`, so MUI won't use the column's valueGetter.
    // We re-order rows here by verification status when the user sorts on this
    // column. No backend enum for verification status yet (v1 scope).
    const activeSort = sortModel[0]
    if (activeSort?.field === 'receipts') {
      const dir = activeSort.sort === 'desc' ? -1 : 1
      const NONE_KEY = verificationSortKey(undefined)
      const keyFor = (tx?: TransactionListModel): number => {
        const hasAny = (tx?.attachmentsCount ?? 0) > 0
        if (!hasAny) return NONE_KEY
        const status =
          tx?.receiptMatchingStatus ??
          mockTransactionVerificationStatus(tx?.id, tx?.attachmentsCount)
        return verificationSortKey(status)
      }
      return [...base].sort((a, b) => {
        const ka = keyFor(a.transaction)
        const kb = keyFor(b.transaction)
        // Rows with no attachments ("none") always sort to the end regardless
        // of direction — they carry no actionable verification signal.
        if (ka === NONE_KEY && kb !== NONE_KEY) return 1
        if (kb === NONE_KEY && ka !== NONE_KEY) return -1
        return dir * (ka - kb)
      })
    }
    return base
  }, [props.transactions, expenseCategoryDataMemo, sortModel])

  // ---- Inline EC edit commit
  const processRowUpdate = useCallback(
    async (
      newRow: TransactionsDataGridRowModel & { [key: string]: any },
      oldRow: TransactionsDataGridRowModel,
      _: { rowId: GridRowId },
    ): Promise<TransactionsDataGridRowModel> => {
      const changedEcField = Object.keys(newRow).find((k) =>
        k.startsWith('ec:'),
      )
      if (!changedEcField) return Promise.resolve(oldRow)

      const expenseCategoryId = changedEcField.substring(3)
      const value = newRow[changedEcField]
      const labelId = typeof value === 'object' ? value?.value : value
      const labelName = typeof value === 'object' ? value?.label : value

      return postCategoryLabel({
        transactionId: oldRow.transaction.id,
        categoryId: expenseCategoryId,
        body: { labelId: labelId || undefined },
      })
        .catch((e) => {
          dispatchErrorToast(e)
          return oldRow
        })
        .then(() => {
          const expCat = (orgExpCats || []).find(
            (ec) => ec.id === expenseCategoryId,
          )
          if (
            props.context === TransactionReportType.AccountingTransactions &&
            expCat?.requiredForTransactions
          ) {
            setTimeout(() => {
              queryClient.invalidateQueries({
                queryKey: transactionsQueryKeys.accountingStatistics(
                  props.filters,
                ),
              })
            }, 3000)
          }
          return {
            ...newRow,
            expenseCategoryCellData: {
              ...newRow.expenseCategoryCellData,
              [changedEcField as ExpenseCategoryFieldKey]: {
                ...newRow.expenseCategoryCellData[
                  changedEcField as ExpenseCategoryFieldKey
                ],
                valueText: labelName,
                labelIdOrFreeText: labelId,
              },
            },
          }
        })
    },
    [postCategoryLabel],
  )

  const handleCellEditStop = useCallback(
    (
      _: GridCellEditStopParams<TransactionsDataGridRowModel>,
      event: MuiEvent,
    ) => {
      resetPostCategoryLabel()
      // We render custom edit cells; prevent default MUI handlers.
      event.defaultMuiPrevented = true
    },
    [resetPostCategoryLabel],
  )

  // ---- Column visibility
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      date: props.defaultColumnVisibilityModel.date,
      merchant: props.defaultColumnVisibilityModel.merchant,
      originalMerchant: props.defaultColumnVisibilityModel.originalMerchant,
      card: props.defaultColumnVisibilityModel.card,
      cardUser: props.defaultColumnVisibilityModel.cardUser,
      team: props.defaultColumnVisibilityModel.team,
      cardholderEmail: props.defaultColumnVisibilityModel.cardholderEmail,
      status: props.defaultColumnVisibilityModel.status,
      managerReviewStatus:
        props.defaultColumnVisibilityModel.managerReviewStatus,
      finalReview: props.defaultColumnVisibilityModel.finalReview,
      amount: props.defaultColumnVisibilityModel.amount,
      currency: props.defaultColumnVisibilityModel.currency,
      authBillingAmountCents:
        props.defaultColumnVisibilityModel.authBillingAmountCents,
      authBillingCurrency:
        props.defaultColumnVisibilityModel.authBillingCurrency,
      authMerchantAmountCents:
        props.defaultColumnVisibilityModel.authMerchantAmountCents,
      authMerchantCurrency:
        props.defaultColumnVisibilityModel.authMerchantCurrency,
      authExchangeRate: props.defaultColumnVisibilityModel.authExchangeRate,
      attachmentsCount: props.defaultColumnVisibilityModel.attachmentsCount,
      creditCardDisplayName:
        props.defaultColumnVisibilityModel.creditCardDisplayName,
      creditCardType: props.defaultColumnVisibilityModel.creditCardType,
      receipts: props.defaultColumnVisibilityModel.receipts,
      txnSynced: props.defaultColumnVisibilityModel.txnSynced,
      receiptsSynced: props.defaultColumnVisibilityModel.receiptsSynced,
    })
  const resetColumnOrderRef = React.useRef<(() => void) | undefined>(undefined)
  const buildDefaultColumnVisibilityModel = useCallback(
    (): GridColumnVisibilityModel =>
      columns.reduce<GridColumnVisibilityModel>((acc, column) => {
        if (column.field.startsWith('ec:')) {
          acc[column.field] =
            props.defaultColumnVisibilityModel.expenseCategories ?? true
          return acc
        }

        const defaultVisibility = (
          props.defaultColumnVisibilityModel as Record<
            string,
            boolean | undefined
          >
        )[column.field]
        acc[column.field] = defaultVisibility ?? true
        return acc
      }, {}),
    [columns, props.defaultColumnVisibilityModel],
  )

  useEffect(() => {
    if (ecLoaded) {
      // Update column visibility model with EC columns from defaults once orgExpCats are loaded.
      setColumnVisibilityModel((prevModel) => ({
        ...prevModel,
        ...orgExpCats?.reduce((acc, curr) => {
          const key = `ec:${curr.id}`
          return {
            ...acc,
            // we will never be able to default show an individual EC
            // since they are keyed using EC ids,
            // so fall back to the general expenseCategories default
            [key]: props.defaultColumnVisibilityModel.expenseCategories,
          }
        }, {}),
      }))
    }
  }, [ecLoaded])

  useEffect(() => {
    if (!ecLoaded || !props.savedVisibleColumns) return

    const visibleColumns = new Set(props.savedVisibleColumns)
    const nextVisibilityModel = columns.reduce<GridColumnVisibilityModel>(
      (acc, column) => {
        acc[column.field] = visibleColumns.has(column.field)
        return acc
      },
      {},
    )

    const currentKeys = Object.keys(columnVisibilityModel)
    const nextKeys = Object.keys(nextVisibilityModel)
    const isSame =
      currentKeys.length === nextKeys.length &&
      nextKeys.every(
        (key) =>
          columnVisibilityModel[key] ===
          nextVisibilityModel[key as keyof GridColumnVisibilityModel],
      )

    if (isSame) return

    setColumnVisibilityModel(nextVisibilityModel)
  }, [ecLoaded, props.savedVisibleColumns, columns, columnVisibilityModel])

  const resetColumnsToDefault = useCallback((): void => {
    if (!ecLoaded) return

    const nextVisibilityModel = buildDefaultColumnVisibilityModel()
    setColumnVisibilityModel(nextVisibilityModel)
    resetColumnOrderRef.current?.()
  }, [ecLoaded, buildDefaultColumnVisibilityModel])

  const handleRegisterResetColumnOrder = useCallback(
    (reset: () => void): void => {
      resetColumnOrderRef.current = reset
    },
    [],
  )

  const handleUserColumnOrderChange = useCallback((): void => {
    props.onUserChangedColumns?.()
  }, [props.onUserChangedColumns])

  const normalizeVisibilityModel = useCallback(
    (model: GridColumnVisibilityModel): GridColumnVisibilityModel =>
      columns.reduce<GridColumnVisibilityModel>((acc, column) => {
        // In MUI DataGrid visibility model, a missing field is treated as visible.
        acc[column.field] = model[column.field] ?? true
        return acc
      }, {}),
    [columns],
  )

  const matchesSavedViewVisibility = useCallback(
    (model: GridColumnVisibilityModel): boolean => {
      if (!props.savedVisibleColumns) return false
      const visibleSet = new Set(props.savedVisibleColumns)
      const normalizedModel = normalizeVisibilityModel(model)
      return columns.every(
        (column) =>
          Boolean(normalizedModel[column.field]) ===
          visibleSet.has(column.field),
      )
    },
    [props.savedVisibleColumns, columns, normalizeVisibilityModel],
  )

  useEffect(() => {
    props.onRegisterResetColumnsToDefault?.(resetColumnsToDefault)
  }, [props.onRegisterResetColumnsToDefault, resetColumnsToDefault])

  // ---- Render (wait for ECs so columns/rows match)
  if (!ecLoaded) return <></>

  return (
    <DataGrid
      apiRef={props.apiRef}
      height={props.clientHeight}
      rowHeight={DataGridRowHeight.Default}
      rows={rows}
      columns={columns}
      infiniteScrollProps={props.infiniteScrollProps}
      filtersBody={props.filters}
      disableRowSelectionOnClick
      sortModel={sortModel}
      onSortModelChange={(m) => setSortModel(m)}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(newModel) => {
        const normalizedModel = normalizeVisibilityModel(newModel)
        setColumnVisibilityModel(normalizedModel)
        if (!matchesSavedViewVisibility(normalizedModel)) {
          props.onUserChangedColumns?.()
        }
        analytics?.track('transactions column selector', {
          screen: calculatedBackgroundLocation.pathname?.replace?.(
            /\/cc_[a-zA-Z0-9]+/,
            '',
          ),
        })
      }}
      initialState={{ pinnedColumns: pinned }}
      orderedColumnFields={props.savedVisibleColumns}
      onRegisterResetColumnOrder={handleRegisterResetColumnOrder}
      onUserColumnOrderChange={handleUserColumnOrderChange}
      alwaysVisibleColumns={['date']}
      disableColumnFilter
      checkboxSelection={props.checkboxProps !== undefined}
      keepNonExistentRowsSelected
      rowSelectionModel={props.rowSelectionModel}
      onRowSelectionModelChange={props.checkboxProps?.handleCheckboxClick}
      isRowSelectable={(params): boolean => {
        const tx = params.row.transaction
        return !!props.checkboxProps?.canSelectTransaction(tx)
      }}
      onRowClick={(row, event): void => {
        if (event.defaultPrevented) return
        const tx = row.row.transaction
        if (tx?.isSplit) {
          queryClient.removeQueries({
            queryKey: transactionsQueryKeys.detail(tx.id),
          })
        }
        dispatch(
          connectedPush({
            pathname: transactionDetailUrl(tx?.id),
            search: '',
            hash: '',
            state: {},
          }),
        )
        analytics?.track(
          'transactions - visit transaction with ' +
            tx.status.toLowerCase() +
            ' status - list item press',
          {
            transactionStatus: tx?.status,
            intent: 'visit transaction',
            interaction: 'list item press',
            screen: calculatedBackgroundLocation.pathname,
            transactionId: tx?.id,
          },
        )
      }}
      processRowUpdate={processRowUpdate}
      editMode={EDIT_MODE}
      onCellEditStop={handleCellEditStop}
      isCellEditable={(params) => {
        if (props.context === TransactionReportType.ExpenseReport) return false
        if (!String(params.field).startsWith('ec:')) return false
        return canEditExpenseCategory({
          transaction: params.row.transaction,
          activeIntegration,
        })
      }}
    />
  )
}

export default TransactionsDataGrid
